import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import type { CoverEffectType } from 'fym-shared/src/music24/coverDoc';
import { TypedMemo } from '../../common/types';
import { coverEditorMessages, effectMessages } from '../coverEditorMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover editor; each part uses its own slice of it */
import styles from '../CoverEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** One row: an effect (its type names it) or a text box (its words name it). */
export interface OrderRow {
  id: string;
  effectType?: CoverEffectType;
  text?: string;
  note?: string;
}

interface OrderListProps {
  /** In draw order: the first row is drawn first (it is under the others). */
  rows: OrderRow[];
  selectedId: string | null;
  disabled: boolean;
  onSelect: (id: string) => void;
  onMove: (id: string, toIndex: number) => void;
  onRemove?: (id: string) => void;
}

/**
 * A list that sets a draw order (design cover-editor d3, d5): the effects, or the text boxes of the
 * Layers panel. The top row is on top of the cover. The arrows move a row; every row works with
 * the keyboard.
 */
const OrderListComponent: React.FC<OrderListProps> = ({ rows, selectedId, disabled, onSelect, onMove, onRemove }) => {
  const intl = useIntl();
  // The list shows the top layer first; the document holds the bottom layer first.
  const shown = useMemo(() => [...rows].reverse(), [rows]);
  return (
    <ul className={styles.layers}>
      {shown.map((row) => {
        const index = rows.findIndex((item) => item.id === row.id);
        const name =
          row.effectType === undefined ? (row.text ?? '') : intl.formatMessage(effectMessages[row.effectType].name);
        return (
          <li key={row.id} className={clsx(styles.layer, row.id === selectedId && styles.layerOn)}>
            <button type="button" className={styles.layerName} onClick={() => onSelect(row.id)}>
              <span className={styles.layerText}>{name}</span>
              {row.note !== undefined && <small>{row.note}</small>}
            </button>
            <button
              type="button"
              className={styles.layerButton}
              aria-label={intl.formatMessage(coverEditorMessages.layerUp, { name })}
              disabled={disabled || index >= rows.length - 1}
              onClick={() => onMove(row.id, index + 1)}
            >
              ↑
            </button>
            <button
              type="button"
              className={styles.layerButton}
              aria-label={intl.formatMessage(coverEditorMessages.layerDown, { name })}
              disabled={disabled || index <= 0}
              onClick={() => onMove(row.id, index - 1)}
            >
              ↓
            </button>
            {onRemove !== undefined && (
              <button
                type="button"
                className={styles.layerButton}
                aria-label={intl.formatMessage(coverEditorMessages.layerRemove, { name })}
                disabled={disabled}
                onClick={() => onRemove(row.id)}
              >
                ×
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export const OrderList = TypedMemo(OrderListComponent);
